import { useState } from 'react'
import { CalendarClock, X } from 'lucide-react'
import type { Appointment } from '@/services/appointmentsApi'
import { appointmentsApi } from '@/services/appointmentsApi'
import { useAuthStore } from '@/store/authStore'
import { useVerticalUI } from '@/lib/verticalUI'

interface RescheduleAppointmentModalProps {
  appointment: Appointment
  onClose: () => void
  onRescheduled: () => void
}

/**
 * Modal de reprogramación (fase A3): la cita actual pasa a RESCHEDULED y se
 * crea una nueva solicitud PENDING para que el staff asigne el slot real.
 */
export default function RescheduleAppointmentModal({
  appointment,
  onClose,
  onRescheduled,
}: RescheduleAppointmentModalProps) {
  const { profile } = useVerticalUI()
  const currentUser = useAuthStore((s) => s.user)

  const [newRequestedSlotText, setNewRequestedSlotText] = useState<string>('')
  const [customMessage, setCustomMessage] = useState<string>(
    () =>
      `${appointment.patientName ?? 'Hola'}, hubo un cambio de disponibilidad con tu ${profile.terminology.primaryTransactionSingular.toLowerCase()} de ${
        appointment.serviceName ?? profile.terminology.itemSingularName.toLowerCase()
      }. ¿Te funciona reagendarla?`
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!newRequestedSlotText.trim()) {
      setError('Indica la nueva preferencia horaria')
      return
    }
    if (!currentUser?.id) {
      setError('Sesión inválida')
      return
    }
    setSaving(true)
    try {
      await appointmentsApi.rescheduleAppointment(appointment.id, {
        newRequestedSlotText: newRequestedSlotText.trim(),
        staffUserId: currentUser.id,
        customMessage: customMessage.trim() || undefined,
      })
      onRescheduled()
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'No se pudo reprogramar la cita')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-amber-500" />
            <h2 className="text-base font-semibold text-slate-800">
              Reprogramar {profile.terminology.primaryTransactionSingular.toLowerCase()}
            </h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">
              Nueva preferencia del {profile.terminology.customerRoleLabel.toLowerCase()}
            </span>
            <input
              type="text"
              value={newRequestedSlotText}
              onChange={(e) => setNewRequestedSlotText(e.target.value)}
              placeholder="ej. el próximo martes en la mañana"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-700 focus:border-amber-500 focus:outline-none"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">
              Mensaje al {profile.terminology.customerRoleLabel.toLowerCase()} (editable)
            </span>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-700 focus:border-amber-500 focus:outline-none"
            />
          </label>

          {error && <p className="text-sm font-medium text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cerrar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-50"
            >
              {saving ? 'Reprogramando…' : 'Reprogramar y avisar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
